export interface ServiceWorkerUpdateOptions {
  /** The browser's service worker container; absent where service workers are unsupported. */
  container?: Pick<ServiceWorkerContainer, 'controller' | 'addEventListener'>
  /** True once the app is usable, so a reload could throw away what someone is doing. */
  isAppReady: () => boolean
  /** Asks the user to reload when a new version takes over a page that is in use. */
  onUpdateAvailable: () => void
  reload: () => void
}

/**
 * Brings open pages onto a new release once its service worker takes control.
 *
 * The worker activates itself (workbox `skipWaiting` + `clientsClaim`), which fires
 * `controllerchange` here. While the app is still booting nothing can be lost, so it reloads
 * straight away: a returning visitor lands on the new build instead of the cached one. Once
 * the app is in use it only offers the reload, so nobody loses a half-filled form.
 */
export function watchServiceWorkerUpdates({ container, isAppReady, onUpdateAvailable, reload }: ServiceWorkerUpdateOptions): void {
  if (!container) return

  // The very first install also claims the page, but that is not a new version to switch to.
  const hadController = Boolean(container.controller)
  let handled = false

  container.addEventListener('controllerchange', (): void => {
    if (!hadController || handled) return
    handled = true

    if (isAppReady()) {
      onUpdateAvailable()
    } else {
      reload()
    }
  })
}
